'use client';

import { yupResolver } from '@hookform/resolvers/yup';
import { Box, Card, Container, Typography } from '@mui/material';
import Grid from '@mui/material/Grid2';
import FormProvider from 'components/hook-form/form-provider';
import { AppointmentContext } from 'context/AppointmentContext';
import { useTranslate } from "locales";
import moment from 'moment';
import { useContext, useState } from 'react';
import { useForm } from 'react-hook-form';
import { PASSING_DATE_FORMAT } from 'utils/constant';
import * as yup from "yup";
import BookingDateSelection from './BookingDateSelection';
import BookingForm from './BookingForm';
import BookingTimeslotSelection from './BookingTimeslotSelection';
import CompanyInfo from './CompanyInfo';

export default function NewAppointmentForm({ referenceData }) {
    const { t } = useTranslate();
    const { companyInformation, CreateAppointment, appointmentSuccess, } = useContext(AppointmentContext);
    const { timezone } = companyInformation || {};

    const validationSchema = yup.object().shape({
        date: yup.date().typeError(t('date_is_invalid_format')).required(`${t("date")} ${t("is_required")}`),
        from_time: yup.date().typeError(t('date_is_invalid_format')).required(`${t("from_time")} ${t("is_required")}`),
        to_time: yup.date().min(yup.ref('from_time'), `${t('to_time')} ${t('must_be_later_than')} ${t('from_time')}`).required(`${t('to_time')} ${t('is_required')}`).typeError(`${t('date_is_invalid_format')}`),
        first_name: yup.string().required(`${t("first_name")} ${t("is_required")}`),
        country: yup.object().required(`${t("country")} ${t("is_required")}`),
        state: yup.object().required(`${t("state")} ${t("is_required")}`),
        city: yup.string().required(`${t("city")} ${t("is_required")}`),
        address_line1: yup.string().required(`${t("address_line")} ${t("is_required")}`),
        make: yup.string().required(`${t("make")} ${t("is_required")}`),
        model: yup.string().required(`${t("model")} ${t("is_required")}`),
        license_plate: yup.string().required(`${t("license_plate")} ${t("is_required")}`),
        phone: yup.string().required("Phone number is required")
            .test('is-valid-phone', function (value, context) {
                const { phone_length_min, phone_length_max } = context?.parent?.country || {};
                if (!value) return true;
                if (phone_length_min && (value.length < phone_length_min || value.length > phone_length_max)) {
                    return this.createError({ message: `Phone number must be between ${phone_length_min} and ${phone_length_max} digits.`, });
                }
                return true;
            }),
        vehicle_classification_type: yup.object().required(`${t("body_type")} ${t("is_required")}`),
    });

    const [loading, setLoading] = useState(false);
    const defaultValues = {
        date: null,
        from_time: null,
        to_time: null,
        address_line1: "",
        country: companyInformation?.country || "",
        state: companyInformation?.state || null,
        city: companyInformation?.city_name,
        showForm: false,
        notes: '',
        first_name: '',
        last_name: '',
        company_name: '',
        phone: '',
        email: '',
        make: '',
        model: '',
        license_plate: '',
        vehicle_classification_type: null
    }

    const methods = useForm({
        resolver: yupResolver(validationSchema),
        defaultValues
    });

    const {
        control,
        setValue,
        watch,
        reset,
        handleSubmit,
        formState: { isSubmitting, isSubmitSuccessful, isDirty, dirtyFields },
    } = methods;


    async function onSubmit(values) {
        const data = {
            application_id: 1,
            timezone: timezone,
            date: values.date ? moment(values.date).format(PASSING_DATE_FORMAT) : "",
            from_date_time: values.from_time ? moment(values.from_time).format("HH:mm") : "",
            to_date_time: values.to_time ? moment(values.to_time).format("HH:mm") : "",
            notes: values?.notes || "",
            first_name: values?.first_name || "",
            last_name: values?.last_name || '',
            company_name: values?.company_name || "",
            phone: values?.phone || "",
            email: values?.email || "",
            city: values?.city || "",
            address_line1: values?.address_line1 || "",
            make: values?.make || "",
            model: values?.model || "",
            // fuel_type: values?.fuel_type,
            license_plate: values?.license_plate,
            // vin: values?.vin,
            country_id: values.country?.country_id,
            state_id: values?.state?.state_id || '',
            state_name: values?.state?.name || '',
            vehicle_classification_type_id: values.vehicle_classification_type?.vehicle_classification_type_id || '',
        };
        await CreateAppointment(data);
    }

    const gradientBorder = {
        p: "2px",
        borderRadius: { xs: 0, md: 3 },
        width: { xs: "100%", md: "auto" },
        background: "linear-gradient(120deg,#FCA5A5 0%,#FDE047 25%,#D8B4FE 50%,#86EFAC 75%,#93C5FD 100%)",
    };

    const appointmentDate = watch("date");
    const showForm = watch("showForm");

    return (
        <>
            <Box sx={{ minHeight: "100vh", display: "flex", justifyContent: "center", alignItems: "center", bgcolor: "#F5F5F4", }}>
                {!isSubmitSuccessful ? (
                    <FormProvider methods={methods} autoComplete="off" onSubmit={handleSubmit(onSubmit)}>
                        <Box sx={gradientBorder}>
                            <Card sx={{ width: "100%", borderRadius: { xs: 0, md: 3 }, boxShadow: "none", overflow: "hidden", bgcolor: "rgba(255, 255, 255, 0.8)" }}>
                                <Container maxWidth='lg' disableGutters >
                                    <Box sx={{ display: "flex", flexDirection: { xs: "column", md: "row" } }}>

                                        <Grid container spacing={0} >
                                            <Grid size={{ xs: 12, md: appointmentDate ? 4 : 7 }}>
                                                <CompanyInfo />
                                            </Grid>
                                            {!showForm &&
                                                <Grid size={{ xs: 12, md: appointmentDate ? 4 : 5 }}>
                                                    <BookingDateSelection
                                                        timezone={timezone}
                                                    />
                                                </Grid>
                                            }
                                            {(appointmentDate && !showForm) &&
                                                <Grid size={{ xs: 12, md: showForm ? 6 : 4 }}>
                                                    <BookingTimeslotSelection
                                                    />
                                                </Grid>
                                            }
                                            {showForm &&
                                                <Grid size={{ xs: 12, md: 8 }}>
                                                    <BookingForm />
                                                </Grid>
                                            }
                                        </Grid>
                                    </Box>
                                </Container>
                            </Card>
                        </Box>
                    </FormProvider>
                ) : (
                    <Container maxWidth='md' sx={{ bgcolor: 'background.theme', display: "flex", justifyContent: "center", alignItems: "center", borderRadius: 5, minHeight: "100vh" }}>
                        <Box textAlign="center" py={6}>
                            <Typography variant="h3" fontWeight="bold" sx={{ mb: 2 }}> {t('appointment_success')} </Typography>
                            <Typography variant="h5">
                                {t('thank_you')} <br />
                                {t('appointment_confirmation')}
                            </Typography>
                            <Typography variant="body1" sx={{ mt: 1 }}> {t('contact_you_shortly')} </Typography>
                        </Box>
                    </Container>
                )}
            </Box>
        </>
    )
}